'use client';

import * as React from 'react';

import { toast } from 'sonner';

type UseCopyToClipboardOptions = {
  timeout?: number;
  successMessage?: string;
  errorMessage?: string;
};

type CopyOptions = {
  showToast?: boolean;
  preferDeferred?: boolean;
};

type CopyValue = string | Promise<string | null | undefined> | null | undefined;

export function useCopyToClipboard({
  timeout = 2000,
  successMessage = 'Copied to clipboard',
  errorMessage = 'Failed to copy to clipboard'
}: UseCopyToClipboardOptions = {}) {
  const [isCopied, setIsCopied] = React.useState(false);
  const resetTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const noValueErrorMessage = 'No value provided to copy to clipboard';

  React.useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const setCopiedState = (showToast: boolean): void => {
    setIsCopied(true);

    if (showToast) {
      toast.success(successMessage);
    }

    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    resetTimeoutRef.current = setTimeout(() => {
      setIsCopied(false);
      resetTimeoutRef.current = null;
    }, timeout);
  };

  const copyValueToClipboard = async (
    value: CopyValue,
    { showToast = true, preferDeferred = false }: CopyOptions = {}
  ): Promise<boolean> => {
    if (typeof window === 'undefined') {
      return false;
    }

    const canWriteDeferredText =
      typeof navigator.clipboard?.write === 'function' && typeof ClipboardItem !== 'undefined';
    const canWriteText = typeof navigator.clipboard?.writeText === 'function';

    if (!canWriteDeferredText && !canWriteText) {
      return false;
    }

    try {
      const textPromise = Promise.resolve(value).then((resolvedValue) => {
        if (!resolvedValue) {
          throw new Error(noValueErrorMessage);
        }

        return resolvedValue;
      });

      const shouldUseDeferredWrite = (preferDeferred || !canWriteText) && canWriteDeferredText;

      if (shouldUseDeferredWrite) {
        try {
          // Start the clipboard write during the user gesture and let the value resolve later.
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': textPromise.then(
                (resolvedValue) => new Blob([resolvedValue], { type: 'text/plain' })
              )
            })
          ]);
        } catch (error) {
          if (!canWriteText) {
            throw error;
          }

          await navigator.clipboard.writeText(await textPromise);
        }
      } else {
        await navigator.clipboard.writeText(await textPromise);
      }

      setCopiedState(showToast);
      return true;
    } catch (e) {
      if (e instanceof Error && e.message === noValueErrorMessage) {
        return false;
      }

      if (showToast) {
        toast.error(errorMessage);
        console.error(e);
      }

      return false;
    }
  };

  const copyToClipboard = (value: CopyValue, options?: CopyOptions): void => {
    if (!value) {
      return;
    }

    void copyValueToClipboard(value, options);
  };

  const copyDeferredToClipboard = (value: CopyValue, options?: CopyOptions): Promise<boolean> =>
    copyValueToClipboard(value, { ...options, preferDeferred: true });

  return { isCopied, copyToClipboard, copyDeferredToClipboard };
}
